import { FC, useMemo } from 'react';
import { AxiosError } from 'axios';
import { Dialog, Box } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { GenerativeModel } from 'entities/generative-models/types';
import { postGenerativeModel, patchGenerativeModel } from 'entities/generative-models/api';
import { CategoryTypeEnum } from 'entities/Files/types';
import { Button } from 'shared/ui/Button';
import { Paper } from 'shared/ui/Paper';
import { InputField } from 'shared/ui/InputField';
import { CategoryCard } from 'widgets/startRecognition/ui/CategoryCard';
import { CategoriesList } from 'widgets/startRecognition/consts/CategoryList';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';

type GenerativeModalProps = {
	open: boolean;
	onClose: () => void;
	modelDetails?: GenerativeModel;
};

const GenerativeModelValidationSchema = yup.object({
	name: yup.string().required('shared_texts.field_is_required'),
});

const GenerativeModal: FC<GenerativeModalProps> = ({ open, onClose, modelDetails }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const category = useMemo(
		() => CategoriesList.find((item) => item.id === CategoryTypeEnum.generative_model),
		[],
	);

	const { mutate: patchGenerative, isLoading: patchIsLoading } = useMutation({
		mutationKey: ['patch/generative'],
		mutationFn: patchGenerativeModel,
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: ['get/generative-models'] });
			onClose();
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: postGenerative, isLoading: postIsLoading } = useMutation({
		mutationKey: ['post/generative'],
		mutationFn: postGenerativeModel,
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: ['get/generative-models'] });
			onClose();
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const handleSubmit = (data: yup.InferType<typeof GenerativeModelValidationSchema>) => {
		if (modelDetails) {
			patchGenerative({ generativeModelId: modelDetails._id, data });
		} else {
			postGenerative(data);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			PaperComponent={Paper}
			PaperProps={{
				title: modelDetails
					? (t('custom_model.create_edit_modal.edit_title') as string)
					: (t('custom_model.create_model') as string),
			}}
		>
			{category && (
				<Box sx={{ mb: '24px' }}>
					<CategoryCard category={category} />
				</Box>
			)}

			<Formik
				initialValues={{
					name: modelDetails?.name || '',
				}}
				validationSchema={GenerativeModelValidationSchema}
				onSubmit={handleSubmit}
			>
				<Form>
					<InputField
						name={'name'}
						label={t('custom_model.create_edit_modal.label')}
						placeholder={t('custom_model.create_edit_modal.placeholder') as string}
						required
					/>
					<Button type="submit" fullWidth disabled={patchIsLoading || postIsLoading}>
						{modelDetails
							? t('custom_model.create_edit_modal.edit_title')
							: t('custom_model.create_model')}
					</Button>
				</Form>
			</Formik>
		</Dialog>
	);
};

export default GenerativeModal;
